// @givmo/board — the minute book: reading a board record, its lineage, and its event log.
//
// ══ WHAT THIS SCREEN IS RESPONSIBLE FOR GETTING RIGHT ═══════════════════════════════════════════
//
//   * ABSENCE IS THE ONLY HONEST RENDER OF ABSENCE. Executive-session scoping is applied in SQL, so a
//     closed-session record this reader did not take part in is simply not in the page — and
//     `meta.total` is scoped with it, precisely so a `limit=1` offset walk cannot map the number and
//     position of the sessions they were not in. NOTHING here counts rows against an expected total,
//     no surface says "n records hidden", and no row renders as "restricted". The reader is not
//     entitled to learn that the withheld thing exists. Same rule, same reason, as the packet's
//     server-filtered entries.
//
//   * A POINTER IS A DISCLOSURE, AND IT IS SCOPED LIKE THE RECORD IT NAMES. All five record-to-record
//     pointers — both correction pointers, both governing-document lineage pointers, and
//     `meeting_record_id` — are omitted from the projection when the reader may not follow them. So a
//     BLANKED pointer arrives as a missing field, indistinguishable from a record that never had one,
//     and that is exactly how it must render: {@link recordPointers} returns only what is present, and
//     the panel that draws it has no branch for "there was one here". Even the event NOTES on either
//     side of a correction carry no id, for the same reason.
//
//   * THE TWO LINEAGES ARE DIFFERENT ACTS AND NEVER SHARE A WORD.
//       – CORRECTION lineage (`corrects_record_id` ⇄ `superseded_by_record_id`) says an earlier record
//         was WRONG. It deletes as a UNIT, and it is a hash input on the forward pointer.
//       – VERSION lineage (`supersedes_record_id`) says the board ADOPTED A REPLACEMENT. It does NOT
//         delete as a unit, and it is outside the content hash.
//     A CHECK refuses both on one row, so a record has one kind or the other. Conflating them would
//     tell an organization its board replaced its bylaws when somebody fixed a typo, or the reverse.
//
//   * THE RECORD IS WHAT WAS APPROVED, AND THE EVIDENCE IS WHAT WAS RECORDED. The approval line is
//     plan §3.4's SAFE-list "as approved by [person] on [date]", composed HERE and only here
//     ({@link recordApprovalLine}) from the recorded fields — the approver's name, which kind of actor
//     approved, and the instant in the reader's own time zone (CBG-UX2). It is never extended with a
//     claim about what the approval accomplished. (The backend's `approval_statement` carries the same
//     words for its non-web readers, dated by the UTC day; this surface no longer renders it.)

import { extractList } from '@givmo/api-client/list';
import type {
  BoardRecordApproveRequest,
  BoardRecordEventOut,
  OrgBoardRecordOut,
  OrgBoardOrgListBoardRecordsData,
} from '@givmo/api-client';
import type { BadgeProps } from '@givmo/design-system';
import { displayInstant, displayLongDate, humanize } from '../../lib/display';
import { factorLabel } from '../../lib/factor-words';
import { signatureActLine } from '../consents/consents-decode';
import { approvedByConsentId } from '../consents/minutes-consent-decode';
import { minutesConsentWords as rc9 } from '../consents/minutes-consent-words';
import { oneLine } from '../conflicts/coi-decode';
import { coiWords } from '../conflicts/coi-words';
import { assessmentWords } from '../evaluations/assessment-words';
import { closedMinutesWords } from '../director/closed-minutes-words';
import { proposalWords } from '../proposals/proposal-words';
import { historyWords } from './history-words';
import { recordPersonWords as fix49, seatPerson } from './record-person-words';
import { approvedBySignatures, isOutForSignatures } from '../minutes/minutes-signatures-decode';
import { minutesSignaturesWords as rc10 } from '../minutes/minutes-signatures-words';
import { minutesAskWords } from '../minutes/minutes-ask-words';
import {
  committeeMeetingsWords as cm,
  consentNoLongerKeptWords as noLongerKept,
} from '../committees/committee-meetings-words';
import { returnWords } from '../returns/return-words';

export { displayDate, displayInstant } from '../../lib/display';

/** The Badge's tone union (the design-system doesn't re-export the tone type directly). */
type BadgeTone = NonNullable<BadgeProps['tone']>;

// ── The list ─────────────────────────────────────────────────────────────────────────────────────

/** The minute book's page. Reads either wire shape and never throws. */
export function extractRecords(body: unknown): OrgBoardRecordOut[] {
  return extractList<OrgBoardRecordOut>(body, 'data');
}

/**
 * The records list's query: the generated one, with its server-side TITLE search `q` (CBG-UX1). The
 * search is the SERVER's, never a filter over a loaded page, which would hide every record past the
 * first page — and it is scoped with the same predicate as the page, so a closed session a reader was
 * not in is never found by searching.
 */
export type RecordsListQuery = NonNullable<OrgBoardOrgListBoardRecordsData['query']>;

export function recordsListQuery({
  kind,
  state,
  search,
  limit,
}: {
  kind: string | null;
  state: string | null;
  search: string;
  limit: number;
}): RecordsListQuery {
  const q = search.trim();
  return {
    'page[limit]': limit,
    ...(kind !== null ? { record_kind: kind as NonNullable<RecordsListQuery['record_kind']> } : {}),
    ...(state !== null ? { state: state as NonNullable<RecordsListQuery['state']> } : {}),
    ...(q.length > 0 ? { q } : {}),
  };
}

/**
 * Whether a record is a MEETING's minutes — the meeting's own, or one of its closed sessions' — and
 * whose (CBG-FIX8). Such minutes are edited and approved on the meeting's Minutes page, and the
 * record core's editor, submit and approval refuse them, so the minute book sends the officer there
 * instead of offering those acts. Read from the record's two nullable fields
 * (`minutes_of_meeting_id`, `minutes_of_segment_id`), defensively; `null` for every other record —
 * including a correction of a meeting's minutes, which is an ordinary record.
 */
export function meetingMinutesOf(
  record: OrgBoardRecordOut,
): { meetingId: string; segmentId: string | null } | null {
  const meetingId = record.minutes_of_meeting_id;
  if (typeof meetingId !== 'string' || meetingId.length === 0) return null;
  const segmentId = record.minutes_of_segment_id;
  return {
    meetingId,
    segmentId: typeof segmentId === 'string' && segmentId.length > 0 ? segmentId : null,
  };
}

/**
 * The record kinds the backend's `BoardRecordKind` enum ships, in its order. Drives the filter chips
 * and the import kind picker.
 */
export const RECORD_KINDS: readonly { value: string; label: string }[] = [
  { value: 'meeting', label: 'Meetings' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'resolution', label: 'Resolutions' },
  { value: 'written_consent', label: 'Written consents' },
  { value: 'disclosure', label: 'Disclosures' },
  { value: 'acknowledgment', label: 'Acknowledgments' },
  { value: 'governing_document', label: 'Governing documents' },
  // CBG-YR2-WEB1: a closed year's annual disclosures — who was asked, who submitted and when, written
  // by the close (the backend's one door for the kind). The kind's label, approved in CBG-YR2-COPY1.
  { value: 'disclosure_cycle', label: coiWords.kindLabel },
  // CBG-YR4-ASSESS-WEB: a closed self-assessment's results — counts and comments without names,
  // written by the close (the backend's one door for the kind). The kind's words, approved in
  // CBG-YR4-COPY1.
  { value: 'self_assessment', label: assessmentWords.kindLabel },
];

/**
 * CBG-UX-COPY3's D13 (D-CBG-42 as amended 2026-10-29 08:36 PDT): a person's yearly conflicts of
 * interest form, told from a single disclosure by its payload's format (the backend's
 * `RESPONSE_FORMATS`, `board-coi-questionnaire-response-v1` and `-v2`), the one discriminator, never
 * a new kind: on the wire the kind stays `disclosure`. The officer's read carries the payload; the
 * director's carries its format beside the record (`payload_format`), so both doors read one rule.
 */
const YEARLY_FORM_FORMAT = /^board-coi-questionnaire-response-v\d+$/;

/** The fields a yearly form is told by: the officer's payload, or the director's `payload_format`. */
export interface YearlyFormFields {
  record_kind?: string | null;
  payload?: { [key: string]: unknown } | null;
  payload_format?: string | null;
}

export function isYearlyForm(record: YearlyFormFields): boolean {
  if ((record.record_kind ?? '').toLowerCase() !== 'disclosure') return false;
  const format = record.payload_format ?? record.payload?.format;
  return typeof format === 'string' && YEARLY_FORM_FORMAT.test(format);
}

/** A record's kind as its own line and the book's Type column name it: a yearly form says so (D13). */
export function recordKindLabelOf(record: YearlyFormFields): string {
  return isYearlyForm(record) ? coiWords.formKind : recordKindLabel(record.record_kind);
}

/** The same set in the singular, for a record's own heading. */
export function recordKindLabel(kind: string | null | undefined): string {
  switch ((kind ?? '').toLowerCase()) {
    case 'meeting':
      return 'Meeting';
    case 'minutes':
      return 'Minutes';
    case 'resolution':
      return 'Resolution';
    case 'written_consent':
      return 'Written consent';
    case 'disclosure':
      return 'Disclosure';
    case 'acknowledgment':
      return 'Acknowledgment';
    case 'governing_document':
      return 'Governing document';
    case 'disclosure_cycle':
      return coiWords.kindLabel;
    case 'self_assessment':
      return assessmentWords.kindLabel;
    // CBG-YR5-DIST: a copy of the return provided to the board (DIST's own label).
    case 'return_distribution':
      return returnWords.kindLabel;
    default:
      return humanize(kind);
  }
}

/**
 * What a record's date is CALLED, and the line that says which day to give, by the kind of record
 * (CBG-FIX6). One date — `occurred_on`, the day a record is about and the day its keeping is counted
 * from — named for what it is on each kind, because "the date it is about" left an officer asking which
 * day was meant. Every surface that shows or asks for the date reads it here: the approval dialog, the
 * draft editor, the historical-import dialog, the minutes' approval and the motion that approves them.
 * An unknown or unchosen kind gets the plain "Date".
 */
export interface RecordDateWording {
  label: string;
  hint: string;
}

export function recordDateWording(kind: string | null | undefined): RecordDateWording {
  switch ((kind ?? '').toLowerCase()) {
    case 'governing_document':
      return { label: 'Date adopted', hint: 'The day your board adopted this version.' };
    case 'resolution':
      return { label: 'Decision date', hint: 'The day your board made this decision.' };
    case 'minutes':
      return { label: 'Meeting date', hint: 'The day of the meeting.' };
    default:
      return { label: 'Date', hint: 'The day this happened.' };
  }
}

/**
 * The states a record can be in, in lifecycle order. `imported` is an ENTRY state, not a stage.
 *
 * The words are D-CBG-9's (2026-09-28): an imported record is tagged so it is clear it was not
 * written or executed through the module — "From earlier records" since CBG-UX-COPY2 (D-CBG-42, A5b;
 * "Imported" read as an action, not a standing) — and a submitted one reads "Awaiting approval".
 */
export const RECORD_STATES: readonly { value: string; label: string }[] = [
  { value: 'draft', label: 'Drafts' },
  { value: 'submitted', label: 'Awaiting approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'imported', label: 'From earlier records' },
  { value: 'discarded', label: 'Discarded' },
];

/** How a record's state reads. */
export function recordStateLabel(state: string | null | undefined): string {
  switch ((state ?? '').toLowerCase()) {
    case 'draft':
      return 'Draft';
    case 'submitted':
      return 'Awaiting approval';
    case 'approved':
      return 'Approved';
    case 'imported':
      return 'From earlier records';
    case 'discarded':
      return 'Discarded';
    default:
      return humanize(state);
  }
}

/** A written consent the consent module filed at its completing signature: every director it went
 *  to signed it (CBG-UX-COPY2's A6, both books' word; it read "Filed", which read as filed with the
 *  state). The export's readable copy says it too (`_state_word`). */
export const SIGNED_BY_EVERYONE = 'Signed by everyone';

/** The fields a record's standing is worded from — every surface that shows a record's state has them. */
interface RecordStandingFields {
  state?: string | null;
  record_kind?: string | null;
  approved_by_board_member_id?: string | null;
  /** CBG-FIX146: the director's read says whether a written consent was filed by its signatures,
   *  without the seat that completed it (the officer's read carries the seat). */
  signed_by_everyone?: boolean | null;
  /** CBG-RC10: a closed session's draft minutes out for the signatures of the directors there. */
  out_for_signatures?: boolean | null;
}

/**
 * How a record's state reads, true per kind (CBG-UX3) — the ONE function every surface that shows a
 * record's state calls (the record page, the Minute book, the hold report); the backend's readable
 * copy says the same (`_state_word`).
 *
 * Nobody approves a disclosure — it is recorded through its one door — or a written consent the
 * consent module filed, whose act is every director's signature. So an approved disclosure reads
 * "Recorded", an approved written consent whose approver is a SEAT (the completing signature) reads
 * "Signed by everyone" ({@link SIGNED_BY_EVERYONE}), and an officer's (account) approval of any kind,
 * like every other kind, reads "Approved". The director's book reads it through here too: their
 * record read says whether its signatures filed it (`signed_by_everyone`, CBG-FIX146), never which
 * seat completed it.
 * Display words only: the stored state is unchanged. Other states read as {@link recordStateLabel}.
 */
export function recordStandingLabel(record: RecordStandingFields): string {
  // CBG-RC10 (frame c3): draft minutes out for the signatures of the directors who were in their
  // closed session read so wherever the minute book states them; the stored state stays `draft`.
  if ((record.state ?? '').toLowerCase() === 'draft' && isOutForSignatures(record)) {
    return rc10.outForSignatures;
  }
  if ((record.state ?? '').toLowerCase() !== 'approved') return recordStateLabel(record.state);
  const kind = (record.record_kind ?? '').toLowerCase();
  if (kind === 'disclosure') return 'Recorded';
  // A closed year is recorded by its close, never approved (the backend's readable copy says
  // "recorded"; CBG-YR2-WEB1). The closed year's state word, approved in CBG-YR2-COPY1.
  if (kind === 'disclosure_cycle') return 'Recorded';
  // CBG-YR4-ASSESS-WEB: a self-assessment's results are recorded by its close, never approved (the
  // backend's readable copy says "recorded", as a closed year's does; approved, CBG-YR4-COPY1).
  if (kind === 'self_assessment') return 'Recorded';
  // CBG-YR5-DIST: a copy of the return is recorded by the act that provides it; nobody approved it
  // (the backend's readable copy says "recorded").
  if (kind === 'return_distribution') return 'Recorded';
  if (
    kind === 'written_consent' &&
    (record.approved_by_board_member_id || record.signed_by_everyone === true)
  ) {
    return SIGNED_BY_EVERYONE;
  }
  return 'Approved';
}

/** Badge tone for a state. PURELY VISUAL — the words come from {@link recordStateLabel}. */
export function recordStateTone(state: string | null | undefined): BadgeTone {
  switch ((state ?? '').toLowerCase()) {
    case 'approved':
    case 'imported':
      return 'success';
    case 'discarded':
      return 'neutral';
    default:
      return 'info';
  }
}

/**
 * Whether this row IS the organization's record, rather than something on its way to becoming one.
 *
 * Both arms stated POSITIVELY, mirroring the backend's own `is_the_record()`. A draft is never the
 * record — that is plan §3.3's whole state machine — and a negative test would admit any state a later
 * build adds into the "this is your record" side.
 */
export function isTheRecord(record: OrgBoardRecordOut): boolean {
  const state = (record.state ?? '').toLowerCase();
  return state === 'approved' || state === 'imported';
}

/** Whether the organization entered this as history rather than the module having produced it. */
export function isImported(record: OrgBoardRecordOut): boolean {
  return record.provenance === 'imported';
}

/**
 * The content address an approval echoes, taken from THE READ THAT RENDERED THE CONTENT.
 *
 * ⚠ THE SAME-READ BINDING IS THE APPROVAL-INTEGRITY SEMANTIC, and this function exists to make it a
 * visible argument at the call site rather than something implicit in a component's variable scope.
 * `POST …/records/{id}/approval` requires the approver to echo the hash of the bytes they read, and
 * the backend refuses a stale one with a 409 — so the echo must come from the same response object
 * the approver was shown, never re-fetched at the moment of approval. An echo obtained separately
 * names bytes nobody looked at, which turns the refusal from a proof into a formality.
 *
 * `pending_content_hash` is DERIVED ON THE READ by the authoritative implementation
 * (`project_record` runs the same `record.compute_hash()` the seal runs), which is why this app no
 * longer computes one. It is `null` on a record that already IS the record — the hash is then a fact
 * under its own name in `content_hash` — and null here withdraws the approval affordance rather than
 * sending an empty echo the backend would refuse with a message about the record having changed.
 *
 * The same shape, for the same reason, as `minutes-decode.ts`'s `approvalEcho`.
 */
export function recordApprovalEcho(record: OrgBoardRecordOut | null | undefined): string | null {
  const hash = (record?.pending_content_hash ?? '').trim();
  return hash.length > 0 ? hash : null;
}

// ── The date an approval seals (CBG-FIX6; D-CBG-8) ───────────────────────────────────────────────

/** A bare calendar date as a date input yields it — the only shape the approval may send. */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The date a record is about, or `null` when it has none. */
export function recordDate(record: OrgBoardRecordOut | null | undefined): string | null {
  const date = (record?.occurred_on ?? '').trim();
  return date.length > 0 ? date : null;
}

/** Whether a date field's value is one the approval can send. */
export function isCalendarDate(value: string): boolean {
  return CALENDAR_DATE.test(value.trim());
}

/**
 * The approval body (`BoardRecordApproveRequest`).
 *
 * The backend approves a record only with the date it is about (CBG-FIX6; D-CBG-8) and lets the
 * approver SUPPLY a missing one in the same act: it checks the echo against the stored bytes first,
 * records the date as the approver's own edit, and seals the dated content. It never CHANGES a date
 * the record already carries — a different one is refused — so this sends `occurred_on` ONLY when the
 * record has none, and a dated record's body stays exactly `{ content_hash }`.
 */
export function recordApprovalBody(
  contentHash: string,
  suppliedDate: string | null,
): BoardRecordApproveRequest {
  const date = (suppliedDate ?? '').trim();
  return isCalendarDate(date)
    ? { content_hash: contentHash, occurred_on: date }
    : { content_hash: contentHash };
}

/**
 * The note of where an imported record came from, or null when there is none to say (CBG-UX11,
 * design-284 §2.3: the note is optional, and the record's page then shows only how it came in,
 * `RecordLineageLine`). It sits under its card's heading "Where this came from", so it takes no
 * prefix (CBG-UX-COPY3's I18); the page folds an AI's note behind its one link (CBG-UX12).
 *
 * A module record needs no line: it is the ordinary case, and labelling it would only make the
 * imported label look like a warning rather than a fact about where a document came from.
 */
export function provenanceLine(record: OrgBoardRecordOut): string | null {
  if (record.provenance !== 'imported') return null;
  const note = (record.imported_source_note ?? '').trim();
  return note.length > 0 ? note : null;
}

/**
 * The badge for a record's confidentiality, or null for the ordinary case.
 *
 * Selected POSITIVELY on `standard`, so an unrecognized value FAILS CLOSED into a badge rather than
 * silently rendering as ordinary. That is the same direction the backend's own SQL and Python
 * renderings take, and for the same reason: the permissive default here is the one that mislabels
 * closed-session content as open.
 */
export function confidentialityBadge(
  record: OrgBoardRecordOut,
): { label: string; tone: BadgeTone } | null {
  const value = (record.confidentiality ?? '').toLowerCase();
  if (value === 'standard') return null;
  if (value === 'executive_session') return { label: 'Closed session', tone: 'warning' };
  return { label: humanize(value), tone: 'warning' };
}

// ── Lineage ──────────────────────────────────────────────────────────────────────────────────────

/** Which lineage a pointer belongs to. The two are never rendered under one heading. */
export type LineageKind = 'correction' | 'version' | 'link';

export interface RecordPointer {
  /** A stable key for the testid, so a pointer's row can be selected without depending on order. */
  field: string;
  lineage: LineageKind;
  /** What the pointer says, in that lineage's OWN words. */
  label: string;
  recordId: string;
}

/**
 * Every record-to-record pointer this record actually carries, in a fixed order.
 *
 * ⚠ THE ABSENT ONES ARE NOT IN THE RESULT AND HAVE NO PLACEHOLDER. A pointer the reader may not follow
 * is omitted by the backend's projection, so it arrives here as `null` and leaves here as nothing —
 * identical to a record that never had one. There is deliberately no `{ present: false }` variant and
 * no "restricted" label: rendering the gap would disclose the existence of what the record store
 * withheld, which is the one thing the scoping exists to prevent.
 *
 * `import_batch_id` is NOT here. It names a backfill SESSION rather than a record, it is not
 * confidentiality-scoped, and it is rendered as its own thing (a link to the session) by the caller.
 */
export function recordPointers(record: OrgBoardRecordOut): RecordPointer[] {
  const pointers: RecordPointer[] = [];

  if (record.corrects_record_id) {
    pointers.push({
      field: 'corrects',
      lineage: 'correction',
      label: 'Recorded as a correction of an earlier record.',
      recordId: record.corrects_record_id,
    });
  }
  if (record.superseded_by_record_id) {
    pointers.push({
      field: 'superseded-by',
      lineage: 'correction',
      label: 'A later record was recorded correcting this one.',
      recordId: record.superseded_by_record_id,
    });
  }
  if (record.supersedes_record_id) {
    pointers.push({
      field: 'supersedes',
      lineage: 'version',
      label: 'Recorded as the version replacing an earlier one.',
      recordId: record.supersedes_record_id,
    });
  }
  if (record.adopted_by_record_id) {
    pointers.push({
      field: 'adopted-by',
      lineage: 'link',
      label: 'The record of the act that adopted this.',
      recordId: record.adopted_by_record_id,
    });
  }
  if (record.meeting_record_id) {
    pointers.push({
      field: 'meeting',
      lineage: 'link',
      label: 'The meeting this record is about.',
      recordId: record.meeting_record_id,
    });
  }

  return pointers;
}

/** The pointers belonging to one lineage, so the two are rendered under their own headings. */
export function pointersOfLineage(
  pointers: readonly RecordPointer[],
  lineage: LineageKind,
): RecordPointer[] {
  return pointers.filter((pointer) => pointer.lineage === lineage);
}

/**
 * Whether this record shows a CORRECTION chain the reader can see.
 *
 * Drives which deletion operation the screen offers, because a correction chain is retained and
 * deleted as a UNIT: a single-member request is refused with a 409 naming the chain. Version lineage
 * is deliberately NOT consulted — a version chain is not deleted as a unit, which is exactly why both
 * its pointers are soft refs.
 *
 * A reader whose correction pointer was blanked sees neither, so the screen offers the single-record
 * deletion and renders the backend's refusal verbatim if it comes. That is the correct dead end: the
 * chain operation would refuse them too, on a member they may not read.
 */
export function isInCorrectionChain(record: OrgBoardRecordOut): boolean {
  return Boolean(record.corrects_record_id) || Boolean(record.superseded_by_record_id);
}

// ── The event log ────────────────────────────────────────────────────────────────────────────────

/** The record's own event log, oldest first — the order the acts happened in. */
export function extractRecordEvents(body: unknown): BoardRecordEventOut[] {
  return extractList<BoardRecordEventOut>(body, 'data');
}

/**
 * How one event reads. `orgNoun` is the host's word for the organization (`BoardRuntime.vocab`): the
 * deletion line names who asked for it.
 */
export function eventTypeLabel(eventType: string | null | undefined, orgNoun: string): string {
  switch ((eventType ?? '').toLowerCase()) {
    case 'created':
      return 'Drafted';
    case 'updated':
      return 'Edited';
    case 'submitted':
      return 'Submitted for approval';
    case 'approved':
      return 'Approved';
    case 'imported':
      return 'Imported';
    case 'discarded':
      return 'Discarded';
    case 'corrected':
      return 'Correction recorded';
    case 'superseded':
      return 'Superseded by a correction';
    case 'signed':
      return 'Signed';
    case 'approval_signed':
      // CBG-RC10 (frame c3): a director's signature approving a closed session's minutes.
      return rc10.signedToApproveEvent;
    case 'acknowledged':
      return 'Acknowledged';
    case 'participant_added':
      return 'Someone was recorded as present';
    case 'deleted':
      return `Deleted at the ${orgNoun}’s request`;
    case 'opened':
      // CBG-RC12-READ (frame c3-history): a current director who wasn't in a closed session opened
      // its minutes — in person, or through an AI for that person.
      return closedMinutesWords.opened;
    default:
      return humanize(eventType);
  }
}

/** A `participant_added` event: an account recorded on a closed record's participants. */
function isParticipantAdded(event: BoardRecordEventOut): boolean {
  return (event.event_type ?? '').toLowerCase() === 'participant_added';
}

/**
 * The line an event leads with: its type's words ({@link eventTypeLabel}), except that a person
 * recorded as present is NAMED (CBG-FIX69): "Dana Sample was recorded as present", from the account
 * the history read derives from the event's stored note, or the shipped "Someone was recorded as
 * present" when the name isn't shown. Never the account's id.
 */
export function eventTitle(event: BoardRecordEventOut, orgNoun: string): string {
  if (isParticipantAdded(event)) {
    const name = presentName(event.participant_display_name);
    return name !== null
      ? historyWords.recordedAsPresent(name)
      : historyWords.someoneRecordedAsPresent;
  }
  return eventTypeLabel(event.event_type, orgNoun);
}

/**
 * The event's note, as stored, to print beneath its line — except a `participant_added` note, which
 * names the account by its id ("Recorded ac000002-… as a participant."): {@link eventTitle} names the
 * person instead (CBG-FIX69).
 */
export function eventNote(event: BoardRecordEventOut): string | null {
  if (isParticipantAdded(event)) return null;
  return event.note ? event.note : null;
}

/**
 * CBG-RC10-WITHDRAWN (§S18.2, §S18.3): the quiet second line under a "Signed to approve" event whose
 * signature counts for nothing — its request withdrawn, an earlier version of the minutes signed, or
 * the minutes approved another way. The backend decides it (`approval_signature_mark`, the
 * orchestrator's W-C2) and this only words it; a mark this build doesn't know, or none, says nothing.
 */
export function approvalSignatureMarkLine(event: BoardRecordEventOut): string | null {
  if ((event.event_type ?? '').toLowerCase() !== 'approval_signed') return null;
  const raw: unknown = event.approval_signature_mark;
  return typeof raw === 'string' ? (minutesAskWords.mark[raw] ?? null) : null;
}

/**
 * Who the event records as acting: the person's NAME, in words that keep WHICH kind of actor it was
 * (plan §3.3), read from the event's capture.
 *
 * A record's approver is ONE limb, an account or a roster seat. An EVENT may name both: the seat that
 * acted and an account beside it. The backend writes both on a filed written consent, a director's
 * acknowledgment, a conflict disclosure and a minutes signature, in two shapes the capture tells apart:
 * - **A seat's own act** — confirmed with an emailed code, an authenticator or a passkey — names the
 *   seat and, when it has one, its linked account. The SEAT acted, so it reads "By <person>", never the
 *   account's name.
 * - **An officer's act for a seat** (`manual_on_behalf`) names the officer's account beside the seat,
 *   or no account at all (a consent entered on a director's behalf, minutes approved at a meeting). It
 *   reads "Recorded by <officer> for <person>"; with no account named, the officer is "an officer",
 *   which is what the capture itself records.
 * An event naming only an account is that account's act: "By <name>".
 *
 * CBG-FIX49 (backend `board-officer-roles.md` §16): <person> is the seat as it was on the event's day
 * — the titles it held then, else "a director", else the name alone (`seatPerson`, from the event's
 * `actor_board_member_title_labels` / `actor_board_member_is_director`). It never says "a board
 * member", which in an organization's own usage means a director.
 *
 * The names are resolved by the backend (CBG-UX2) and are cosmetic there: absent when they do not
 * resolve — an account since deleted or with no name on file, a directory that did not answer. The id
 * is never shown in its place (it reads as nobody), and the fallback says only what is true: who it was
 * is not shown. It makes no claim about why.
 */
export function eventActorLine(
  event: BoardRecordEventOut,
  record?: Pick<OrgBoardRecordOut, 'import_origin'>,
): string | null {
  if ((event.event_type ?? '').toLowerCase() === 'opened') return openedActorLine(event);
  // CBG-UX-COPY3's I19: a record an AI proposed was imported by the person who confirmed it, and its
  // history names both, as the card above it does; a record entered by hand keeps "By {name}".
  if (
    (event.event_type ?? '').toLowerCase() === 'imported' &&
    record?.import_origin === 'connected_ai' &&
    event.actor_user_id
  ) {
    return proposalWords.confirmedFromProposal(
      presentName(event.actor_user_display_name) ?? 'someone whose name isn’t shown',
    );
  }
  // CBG-FIX69: RC10's custody catch-up records a participant with no actor; it says who did.
  if (isParticipantAdded(event) && !event.actor_user_id && !event.actor_board_member_id) {
    return historyWords.addedFromTheSession;
  }
  if (event.actor_board_member_id) {
    const person = eventSeatPerson(event) ?? fix49.unnamed;
    if (isOnBehalfCapture(event.actor_auth_method)) {
      const officer = event.actor_user_id
        ? (presentName(event.actor_user_display_name) ?? 'someone whose name isn’t shown')
        : 'an officer';
      return fix49.recordedFor(officer, person);
    }
    return fix49.by(person);
  }
  if (event.actor_user_id) {
    const name = presentName(event.actor_user_display_name);
    return name ? `By ${name}` : 'By someone whose name isn’t shown';
  }
  return null;
}

/**
 * CBG-RC12-READ (frame c3-history): who opened a closed session's minutes they weren't in — "By Theo
 * Barrett, a director who wasn’t in this session", or "Through an AI assistant for Theo Barrett, …"
 * when an AI opened them for that person (D-CBG-45 as amended: the AI that acted is "an AI
 * assistant", never its tool's name). The person is the seat's roster name, else the account's
 * (backend `board_minutes.md` §O4); never an id.
 */
function openedActorLine(event: BoardRecordEventOut): string {
  const name =
    presentName(event.actor_board_member_display_name) ??
    presentName(event.actor_user_display_name) ??
    'someone whose name isn’t shown';
  return presentName(event.opened_through_client_name) !== null
    ? closedMinutesWords.openedThrough(name)
    : closedMinutesWords.openedBy(name);
}

/** The event's seat as it was that day (CBG-FIX49), or null when its name didn't resolve. */
function eventSeatPerson(event: BoardRecordEventOut): string | null {
  return seatPerson(
    presentName(event.actor_board_member_display_name),
    event.actor_board_member_title_labels,
    event.actor_board_member_is_director,
  );
}

/** An officer entered the seat's act for it: the capture method the backend records for that. */
function isOnBehalfCapture(method: string | null | undefined): boolean {
  return (method ?? '').trim().toLowerCase() === 'manual_on_behalf';
}

/**
 * The approval line on a record's page: plan §3.4's "as approved by [person] on [date]", with the
 * date and time the approval was recorded, in the reader's own time zone and the date written out —
 * "As approved by Ruth Calloway, Chair, on September 24, 2026 at 7:20 PM". Every line here says its
 * date the one way (`writtenOutMoment`), the closed year's and a written consent's included.
 *
 * Composed from the recorded fields (the approver's name and facts, which limb approved,
 * `approved_at`) and in this one place. An administrator's approval names the person; a seat's names
 * the person as they were that day (CBG-FIX49, approved 2026-10-13): the titles held then, else "a
 * director", else the name alone — "As approved by Iris Okafor on …" — except where the seat's act was
 * not an approval (CBG-UX3): a filed written consent reads "Signed by every director it went out to
 * (3 of 3); the last signature on …", a disclosure "Disclosed by Maren Lindqvist, Secretary and
 * Treasurer; recorded on …". A name that did not resolve reads "someone whose name isn’t shown",
 * never an id. It says who and when, and nothing about effect.
 *
 * Minutes a written consent approved (CBG-RC9, `approved_by_written_consent_id`) read as the
 * directors' act — "Approved by a written consent signed by every director it went out to; the last
 * signature on …" — never as the one seat whose signature completed it (CBG-UX3's rule): every
 * director it went out to signed, or it would not have executed. Once that consent's own record is no
 * longer kept, the read says so outright (CBG-FIX39, `approved_by_written_consent_kept: false`, the
 * link then null), and so does the line — never the completing seat's, and never inferred from the
 * link's absence.
 *
 * A committee's act (CBG-YR6, the record's own `committee`): its filed written consent is signed by
 * the members of the committee it went out to, and minutes its consent approved say the committee.
 *
 * Null when nobody approved the record here (an import, a draft): the page says that instead.
 */
export function recordApprovalLine(
  record: OrgBoardRecordOut,
  events?: readonly BoardRecordEventOut[],
): string | null {
  const when = writtenOutMoment(record.approved_at);
  const committee = recordCommitteeName(record);
  // CBG-FIX39: the consent approved them, and its own record is no longer kept — said outright.
  if (record.approved_by_written_consent_kept === false && when !== null) {
    return committee !== null ? noLongerKept.committee(committee, when) : noLongerKept.board(when);
  }
  if (approvedByConsentId(record) !== null && when !== null) {
    return committee !== null ? cm.recordApproval(committee, when) : rc9.recordApproval(when);
  }
  // CBG-RC10: minutes the signatures of the directors who were in their closed session approved — the
  // directors' act, never the one seat whose signature came last (CBG-UX3).
  const bySignatures = approvedBySignatures(record);
  if (bySignatures !== null && when !== null) {
    return committee !== null
      ? cm.signaturesRecordApproval(bySignatures.signedCount, when)
      : rc10.recordApproval(bySignatures.signedCount, when);
  }
  const on = (lead: string, gap: string) => (when === null ? lead : `${lead}${gap}${when}`);
  // CBG-YR2-WEB1: a closed year's record is the closing officer's act, and says so as the backend's
  // `approval_statement` does ("Closed by {name} on {day}."), dated here like every approval line.
  // CBG-YR4-ASSESS-WEB: a self-assessment's results likewise, in the same approved words.
  const closedByItsClose = ['disclosure_cycle', 'self_assessment'].includes(
    (record.record_kind ?? '').toLowerCase(),
  );
  if (closedByItsClose && record.approved_by_user_id) {
    return coiWords.closedBy(presentName(record.approved_by_user_display_name), when);
  }
  // CBG-YR5-DIST: a copy of the return is the providing officer's act, dated by the day it was
  // provided (its `occurred_on`), as the backend's `approval_statement` says it.
  if ((record.record_kind ?? '').toLowerCase() === 'return_distribution') {
    const day = displayLongDate(record.occurred_on);
    return returnWords.recordProvidedBy(
      presentName(record.approved_by_user_display_name),
      day === null ? null : oneLine(day),
    );
  }
  if (record.approved_by_user_id) {
    const name = presentName(record.approved_by_user_display_name);
    // A comma sets off a fallback phrase before "on" (CBG-UX3), so the date never reads as part
    // of it; the backend's `approval_statement` says it the same way.
    return name
      ? on(`As approved by ${name}`, ' on ')
      : on('As approved by someone whose name isn’t shown', ', on ');
  }
  if (!record.approved_by_board_member_id) return null;
  const name = presentName(record.approved_by_board_member_display_name);
  const person = seatPerson(
    name,
    record.approved_by_board_member_title_labels,
    record.approved_by_board_member_is_director,
  );
  const kind = (record.record_kind ?? '').toLowerCase();
  if (kind === 'written_consent') {
    // A filed written consent's seat is the signature that COMPLETED it; naming that one
    // director would say one person approved an act that took every director's signature
    // (CBG-UX3). It says what the record carries: its own signature counts.
    return on(
      `Signed by ${
        committee !== null
          ? cm.filedSigners(committee, record.payload?.signed_count, record.payload?.required_count)
          : filedConsentSigners(record.payload)
      }; the last signature`,
      ' on ',
    );
  }
  if (kind === 'disclosure' && isYearlyForm(record)) {
    // CBG-UX-COPY3's D13: a yearly form is signed, in Board or on the paper form an officer recorded
    // (its approval event's capture says which); the line waits for the record's events.
    if (events === undefined) return null;
    const approval = events.find((event) => (event.event_type ?? '').toLowerCase() === 'approved');
    if (approval !== undefined && isOnBehalfCapture(approval.actor_auth_method)) {
      return name !== null
        ? on(coiWords.formRecordedFromPaper(name), ' on ')
        : on(coiWords.formRecordedFromPaperUnnamed, ', on ');
    }
    if (person === null) return on(coiWords.formSignedBy(fix49.unnamed), ', on ');
    return on(coiWords.formSignedBy(person), person === name ? ' on ' : ', on ');
  }
  if (kind === 'disclosure') {
    // A disclosure's seat is the person who made it, and a disclosure is not "approved".
    return on(`Disclosed by ${person ?? fix49.unnamed}`, '; recorded on ');
  }
  if (person === null) return on(`As approved by ${fix49.unnamed}`, ', on ');
  // A phrase of titles, or "a director", is set off by commas; the name alone is not (the backend's
  // `approval_statement` says it the same way).
  return on(`As approved by ${person}`, person === name ? ' on ' : ', on ');
}

/**
 * The committee whose act a record is (CBG-YR6), as the record itself names it — a committee's filed
 * written consent, a committee meeting's minutes — or null for every other record.
 */
export function recordCommitteeName(record: Pick<OrgBoardRecordOut, 'committee'>): string | null {
  const name = (record.committee?.name ?? '').trim();
  if (record.committee == null) return null;
  return name.length > 0 ? name : cm.aCommittee;
}

/**
 * Who signed a filed written consent, from its own record: "every director it went out to (3 of
 * 3)", or "2 of the 3 directors it went out to" should the counts ever disagree. The backend's
 * `filed_consent_statement` words it the same way.
 */
function filedConsentSigners(payload: OrgBoardRecordOut['payload']): string {
  const signed = payload?.signed_count;
  const required = payload?.required_count;
  if (typeof signed !== 'number' || typeof required !== 'number' || required === 0) {
    return 'the directors it went out to';
  }
  return signed === required
    ? `every director it went out to (${signed} of ${required})`
    : `${signed} of the ${required} directors it went out to`;
}

/**
 * The making event's count under "Signed by everyone" (CBG-UX-COPY3's R4): "3 of 3 directors it went
 * out to", the title above it already saying who signed; "the directors it went out to" should the
 * record carry no counts.
 */
function filedConsentCount(payload: OrgBoardRecordOut['payload']): string {
  const signed = payload?.signed_count;
  const required = payload?.required_count;
  if (typeof signed !== 'number' || typeof required !== 'number' || required === 0) {
    return 'the directors it went out to';
  }
  return `${signed} of ${required} directors it went out to`;
}

/**
 * "Aug 28, 2026 at 2:05 PM" in the reader's zone, or null for an absent or unreadable instant. Its day
 * stays on one line wherever the sentence wraps (`oneLine`), as the backend's served dates keep their
 * month and day together, so a phone never splits "Aug" from "28" (the CBG-YR2-COPY2 review fold).
 * The director's opening line says it (`director-records-page.tsx`); a record's approval lines write
 * the date out (`writtenOutMoment`).
 */
export function sentenceMoment(iso: string | null | undefined): string | null {
  return moment(iso, 'medium');
}

/**
 * "August 28, 2026 at 2:05 PM" in the reader's zone, or null for an absent or unreadable instant: the
 * date written out, as every approval line on a record's page says it (CBG-FIX49's approved words
 * write it out), kept on one line as `sentenceMoment`'s day is.
 */
export function writtenOutMoment(iso: string | null | undefined): string | null {
  return moment(iso, 'long');
}

function moment(iso: string | null | undefined, dateStyle: 'medium' | 'long'): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const day = oneLine(date.toLocaleDateString(undefined, { dateStyle }));
  const time = date.toLocaleTimeString(undefined, { timeStyle: 'short' });
  return `${day} at ${time}`;
}

/** A resolved name worth showing, or null — blank is the same as absent. */
function presentName(name: string | null | undefined): string | null {
  const trimmed = (name ?? '').trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * How an act was re-proved, when it was — plan §3.3's provability capture.
 *
 * Rendered as WHAT WAS RECORDED and nothing more. The methods are not equivalent (an authenticator is
 * stronger than an emailed code, which re-proves the same mailbox the link arrived on), so the
 * recorded method is shown rather than flattened into "confirmed".
 *
 * An officer's entry for a seat (`manual_on_behalf`) keeps the identity it was recorded for (the seat's
 * email of record where it has one), which keeps the act attributable after the roster is edited,
 * because the log never is. It drops the method: the actor line already says an officer recorded it.
 * With no identity recorded, there is no line. The event's note, which carries the officer's own note
 * where the event holds one, has its own line on the page and is never read here.
 */
export function eventCaptureLine(event: BoardRecordEventOut): string | null {
  // CBG-RC12-READ: an opening through the director's signing window keeps the window's capture in the
  // log (§O12 C2), but its line is the opening's own words (frame c3-history), never a factor's.
  if ((event.event_type ?? '').toLowerCase() === 'opened') return null;
  const parts: string[] = [];
  const assertion = (event.actor_identity_assertion ?? '').trim();
  if (assertion.length > 0) parts.push(assertion);
  const method = (event.actor_auth_method ?? '').trim();
  if (method.length > 0 && !isOnBehalfCapture(method)) parts.push(captureMethodLabel(method));
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** How a capture method reads. */
export function captureMethodLabel(method: string | null | undefined): string {
  switch ((method ?? '').toLowerCase()) {
    case 'passkey_uv':
    case 'totp':
    case 'email_code':
      // The one set of factor words (CBG-UX3), the consent log's; a passkey is a director's account
      // signing (CBG-SEC2).
      return factorLabel(method) ?? humanize(method);
    case 'manual_on_behalf':
      return 'entered by an officer on their behalf';
    default:
      return humanize(method);
  }
}

/** What the making event of a seat-completed record says in place of the approval words. */
export interface MakingEventWords {
  title: string;
  detail: string;
  actor: string | null;
}

/**
 * The words for the event that made a disclosure or a filed written consent the record (CBG-UX3).
 *
 * That event is logged as `approved`, and read literally it said "Approved · Became approved · By Dana
 * Sample, as a board member": the verb twice, and for a consent it credited the completing signer with
 * the board's act. It is titled with the record's own standing word ({@link recordStandingLabel}), its
 * detail says what happened, and its actor line stays true — the discloser, or the director whose
 * signature completed the consent, worded as the last signature. Null for any other event or kind,
 * which keep their words.
 */
export function makingEventWords(
  record: OrgBoardRecordOut,
  event: BoardRecordEventOut,
): MakingEventWords | null {
  if ((event.event_type ?? '').toLowerCase() !== 'approved') return null;
  // CBG-FIX39: a consent whose own record is no longer kept still made this approval.
  if (approvedByConsentId(record) !== null || record.approved_by_written_consent_kept === false) {
    return consentApprovalWords(event, recordCommitteeName(record));
  }
  if (approvedBySignatures(record) !== null) {
    return signaturesApprovalWords(event, recordCommitteeName(record));
  }
  const standing = recordStandingLabel(record);
  // CBG-YR2-WEB1: a closed year is recorded by its close; nobody disclosed anything in it.
  if ((record.record_kind ?? '').toLowerCase() === 'disclosure_cycle') {
    return { title: standing, detail: coiWords.closedEvent, actor: eventActorLine(event) };
  }
  // CBG-YR4-ASSESS-WEB: a self-assessment's results are recorded by its close; nobody approved them.
  if ((record.record_kind ?? '').toLowerCase() === 'self_assessment') {
    return { title: standing, detail: assessmentWords.closedEvent, actor: eventActorLine(event) };
  }
  // CBG-YR5-DIST: a copy of the return is recorded by the act that provides it.
  if ((record.record_kind ?? '').toLowerCase() === 'return_distribution') {
    return { title: standing, detail: returnWords.providedEvent, actor: eventActorLine(event) };
  }
  if (standing === 'Recorded') {
    // CBG-UX-COPY3's D13: a yearly form's making event says what its person did; a single
    // disclosure keeps its words.
    const detail = isYearlyForm(record) ? coiWords.formMadeEvent : 'Disclosed to the board';
    return { title: standing, detail, actor: eventActorLine(event) };
  }
  if (standing !== SIGNED_BY_EVERYONE) return null;
  // CBG-FIX49: the person as they were on the event's day.
  const who = eventSeatPerson(event) ?? fix49.unnamed;
  let actor: string;
  if (isOnBehalfCapture(event.actor_auth_method)) {
    const officer = event.actor_user_id
      ? (presentName(event.actor_user_display_name) ?? 'someone whose name isn’t shown')
      : 'an officer';
    actor = fix49.lastSignatureRecordedFor(officer, who);
  } else {
    actor = fix49.lastSignature(who);
  }
  // CBG-UX-COPY3's R4: the title above already says who signed ("Signed by everyone"), so the line
  // gives the count without saying "signed" twice.
  return { title: standing, detail: filedConsentCount(record.payload), actor };
}

/**
 * The approval a written consent made of minutes (CBG-RC9): the directors' act — a committee's
 * minutes, its members' (CBG-YR6) — and whose signature completed it — the record's approver limb — in
 * the capture's own terms, an officer's on-behalf entry named as theirs.
 */
function consentApprovalWords(
  event: BoardRecordEventOut,
  committee: string | null,
): MakingEventWords {
  const seat = presentName(event.actor_board_member_display_name);
  let actor: string;
  if (seat === null) {
    actor = rc9.completedByUnnamed;
  } else if (isOnBehalfCapture(event.actor_auth_method)) {
    // "someone whose name isn’t shown" in this line is the approved fallback (CBG-YR3-ROLES-COPY4);
    // the RC9 page showed "…, recorded by an officer, completed the consent".
    const officer = event.actor_user_id
      ? (presentName(event.actor_user_display_name) ?? 'someone whose name isn’t shown')
      : 'an officer';
    actor = rc9.completedByOnBehalf(seat, officer);
  } else {
    actor = rc9.completedBy(seat);
  }
  // An approval event always carries its two states; with none, the one it made.
  return {
    title: committee !== null ? cm.consentEventTitle(committee) : rc9.eventTitle,
    detail: eventTransition(event) ?? 'Became approved',
    actor,
  };
}

/**
 * CBG-RC10: the approval the last signature of the directors who were in a closed session made of its
 * minutes — the directors' act; a committee's closed session, its members' (CBG-YR6) — and whose
 * signature was the last, in the capture's own terms, an officer's entry named as theirs.
 */
function signaturesApprovalWords(
  event: BoardRecordEventOut,
  committee: string | null,
): MakingEventWords {
  const seat = presentName(event.actor_board_member_display_name);
  let actor: string;
  if (seat === null) {
    actor = rc10.lastSignatureUnnamed;
  } else if (isOnBehalfCapture(event.actor_auth_method)) {
    const officer = event.actor_user_id
      ? (presentName(event.actor_user_display_name) ?? 'someone whose name isn’t shown')
      : 'an officer';
    actor = rc10.lastSignatureOnBehalf(seat, officer);
  } else {
    actor = rc10.lastSignature(seat);
  }
  return {
    title: committee !== null ? cm.signaturesEventTitle : rc10.approvalEventTitle,
    detail: eventTransition(event) ?? 'Became approved',
    actor,
  };
}

/** One act of a filed written consent's own signature log, as the record page lists it. */
export interface FiledConsentAct {
  key: string;
  day: string;
  name: string;
  words: string;
}

/**
 * A filed written consent's own signature log, from the record's payload (CBG-UX3): one row per act in
 * the order it was recorded, withdrawals included — "Aug 24, 2026 · Jamie Sample · Withdrawn by the
 * director, confirmed with an authenticator app". The same rows the readable minute book prints; each
 * seat named from the payload's OWN roster (frozen when the consent was filed, so a later rename does
 * not change what the record says), the act in the consent log's words (`signatureActLine`), the day
 * in the reader's zone. Empty for any other record.
 */
export function filedConsentLog(record: OrgBoardRecordOut): FiledConsentAct[] {
  if (recordStandingLabel(record) !== SIGNED_BY_EVERYONE) return [];
  const payload = record.payload ?? {};
  const roster = Array.isArray(payload.roster) ? payload.roster : [];
  const acts = Array.isArray(payload.signatures) ? payload.signatures : [];
  const names = new Map<string, string>();
  for (const member of roster) {
    if (member && typeof member === 'object') {
      const row = member as Record<string, unknown>;
      const name = presentName(typeof row.display_name === 'string' ? row.display_name : null);
      if (name !== null) names.set(String(row.board_member_id), name);
    }
  }
  const rows: FiledConsentAct[] = [];
  acts.forEach((act, index) => {
    if (!act || typeof act !== 'object') return;
    const row = act as Record<string, unknown>;
    const text = (value: unknown) => (typeof value === 'string' ? value : null);
    rows.push({
      key: `${index}-${String(row.board_member_id)}`,
      day: instantDay(text(row.occurred_at)),
      name: names.get(String(row.board_member_id)) ?? fix49.unnamedOpening,
      words: signatureActLine({
        act: text(row.act),
        signatureClass: text(row.signature_class),
        authMethod: text(row.auth_method),
      }),
    });
  });
  return rows;
}

/** An instant's calendar day in the reader's zone ("Aug 24, 2026"), or "—". */
function instantDay(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

/** When the event happened, formatted. */
export function eventInstant(event: BoardRecordEventOut): string {
  return displayInstant(event.occurred_at);
}

/** The state transition an event records, when it records one. */
export function eventTransition(event: BoardRecordEventOut): string | null {
  const from = (event.from_state ?? '').trim();
  const to = (event.to_state ?? '').trim();
  if (from.length === 0 && to.length === 0) return null;
  // An import is a history entry's act ("Imported", which stays: CBG-UX-COPY2's A5b), so its entry
  // keeps "Became imported"; the state's own word, "From earlier records", reads as a status.
  if (from.length === 0) {
    return to.toLowerCase() === 'imported'
      ? 'Became imported'
      : `Became ${recordStateLabel(to).toLowerCase()}`;
  }
  if (to.length === 0) return `Was ${recordStateLabel(from).toLowerCase()}`;
  return `${recordStateLabel(from)} → ${recordStateLabel(to)}`;
}
